import { QueryClient } from '@tanstack/react-query';
import { cacheConfig } from './env';

// Query client configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache time (how long data stays in cache when component unmounts)
      gcTime: cacheConfig.ttl * 2, // 10 minutes default
      
      // Stale time (how long data is considered fresh)
      staleTime: cacheConfig.ttl, // 5 minutes default
      
      // Retry configuration
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch on window focus in production only
      refetchOnWindowFocus: process.env.NODE_ENV === 'production',
      
      // Refetch on reconnect
      refetchOnReconnect: true,
      
      // Background refetch interval (disabled by default)
      refetchInterval: false,
    },
    mutations: {
      // Retry mutations once
      retry: 1,
      
      // Retry delay for mutations
      retryDelay: 1000,
    },
  },
});

// Query keys factory for consistent key management
export const queryKeys = {
  // Auth
  auth: {
    user: ['auth', 'user'] as const,
    profile: ['auth', 'profile'] as const,
  },
  
  // Activation
  activation: {
    all: ['activation'] as const,
    status: (userId: string) => ['activation', 'status', userId] as const,
    history: (userId: string) => ['activation', 'history', userId] as const,
  },
  
  // Playlist
  playlist: {
    all: ['playlist'] as const,
    user: (userId: string) => ['playlist', 'user', userId] as const,
    detail: (playlistId: string) => ['playlist', 'detail', playlistId] as const,
  },
  
  // Reseller
  reseller: {
    all: ['reseller'] as const,
    dashboard: ['reseller', 'dashboard'] as const,
    clients: (filters?: any) => ['reseller', 'clients', filters] as const,
    client: (clientId: string) => ['reseller', 'client', clientId] as const,
    codes: (status?: string) => ['reseller', 'codes', status] as const,
    earnings: (period?: string) => ['reseller', 'earnings', period] as const,
    stats: ['reseller', 'stats'] as const,
  },
} as const;

// Utility functions for cache management
export const cacheUtils = {
  // Invalidate all queries for a specific feature
  invalidateFeature: (feature: keyof typeof queryKeys) => {
    const featureQueries = queryKeys[feature];
    if ('all' in featureQueries) {
      return queryClient.invalidateQueries({
        queryKey: (featureQueries as any).all
      });
    }
    // For auth queries that don't have 'all', invalidate by feature name
    return queryClient.invalidateQueries({
      queryKey: [feature]
    });
  },
  
  // Clear all cache
  clearAll: () => {
    return queryClient.clear();
  },
  
  // Remove specific query from cache
  removeQuery: (queryKey: readonly unknown[]) => {
    return queryClient.removeQueries({ queryKey });
  },
  
  // Prefetch data
  prefetch: async (queryKey: readonly unknown[], queryFn: () => Promise<any>) => {
    return queryClient.prefetchQuery({
      queryKey,
      queryFn,
    });
  },
  
  // Set query data manually
  setQueryData: (queryKey: readonly unknown[], data: any) => {
    return queryClient.setQueryData(queryKey, data);
  },
  
  // Get cached data
  getQueryData: (queryKey: readonly unknown[]) => {
    return queryClient.getQueryData(queryKey);
  },
};
